import { Divider, Grid, TextField, MenuItem, FormHelperText, ListSubheader, CircularProgress, Checkbox } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'
import FlexView from 'react-flexview/lib'
import { useForm, Controller } from 'react-hook-form'
import { EventEmitterService } from '../../services/EventEmitterService'
import { FirebaseConfigKey, FirebaseService } from '../../services/FirebaseService'
import { YesNoRadioButton } from '../Core/Buttons/ControlledRadioButtons'
import NewFillButton from '../Core/NewFillButton'
import { SkyInformationPopover } from '../Core/Popover/SkyPopover'
import { awaitWithLoading, getEstimatedMilesOptions, prange, vinValidation } from '../../services/utils'
import { ReactComponent as MotorcycleSvg } from '../../assets/motorcycle.svg'
import LocalizationService from '../../services/LocalizationService'
import vehiclesApi from '../../api/vehicles.api'
import { GlobalTextField } from '../Core/Input/ControlledTextField'
import { useAdministrativeDivisions } from '../Core/Hooks/useAdministrativeDivision'
import { reportGTM } from '../Core/Utilities/GTagManger'
import { statesWithoutLojackDiscount } from '../../Constants'
import { StatesContextStore } from '../../context/StatesContext'
import { SessionService } from '../../services/SessionService'
import {
  fetchMakesMethod,
  fetchModelsMethod,
  fetchRequestedMotorcyclesMethod,
  fetchYearsMethod,
  getEstimatedToolTip,
  MOST_COMMON_MAKES,
  snwAtvQuestionAdjustments,
  validateOwnershipMethod,
} from './motorcyclesUtils'
import { SessionContextStore } from '../../context/SessionContext'

const most_common_make_upper = MOST_COMMON_MAKES.map(k => k.toUpperCase())

export const MotorcycleForm = ({ defaultValues = {}, state, SAVE_EVENT_KEY, onSave, billingFlowType }) => {
  const { register, watch, triggerValidation, setValue, errors, control, getValues } = useForm({
    defaultValues: {
      ...defaultValues,
      year: defaultValues.year || 'yeardefault',
      make: defaultValues.make || 'makedefault',
      model: defaultValues.model || 'modeldefault',
      type: defaultValues.type,
      estimated_mileage_value: defaultValues.estimated_mileage_value || 0,
    },
    mode: 'onChange',
  })

  const { isLoadingStates, administrativeDivisions: states } = useAdministrativeDivisions()
  const sessionContext = useContext(SessionContextStore)
  const isAdmin = SessionService.isAdmin()
  const [years, setYears] = useState([])
  const [makes, setMakes] = useState([])
  const [models, setModels] = useState([])
  const [showNewVehicleTextField, setShowNewVehicleTextField] = useState(false)
  const [isLojackFactordDisabled, setIsLojackFactordDisabled] = useState(false)
  const [isAntiLockFactorDisabled, setIsAntiLockFactorDisabled] = useState(false)
  const [isNeedOdometerCheck, setIsNeedOdometerCheck] = useState(false)
  const isNewFlow = sessionContext.newPaymentFlow
  const yearWatch = watch('year')
  const makeWatch = watch('make')
  const modelWatch = watch('model')
  const ccWatch = watch('engine_cc')
  const typeWatch = watch('type')
  const otherWatch = watch('new_vehicle_req_details')
  const lienholderWatch = watch('lienholder')
  const [checked, setChecked] = React.useState(false)
  const givenMake = defaultValues.make
  const givenModel = defaultValues.model
  const statesContext = useContext(StatesContextStore)
  const estimatedToolTip = getEstimatedToolTip(statesContext.billing_flow_type_states[state])

  const fetchModels = async (inputMake, inputYear) => {
    let res = await fetchModelsMethod(inputMake, inputYear)
    setModels(res.models)
    setShowNewVehicleTextField(res.showNewVehicleTextField)
    if (givenModel) {
      setValue('model', givenModel)
    }
  }

  const fetchMakes = async inputYear => {
    let res = await fetchMakesMethod(inputYear)
    setMakes(res)
    if (givenMake) {
      setValue('make', givenMake)
    }
  }

  const fetchYears = async () => {
    let res = await fetchYearsMethod()
    setYears(res)
  }

  const fetchRequestedMotorcycles = async (model, year) => {
    let res = await fetchRequestedMotorcyclesMethod(model, year)
    setValue([{ engine_cc: res.enginecCC }, { type: res.type }])
  }

  useEffect(() => {
    let res = snwAtvQuestionAdjustments(typeWatch)
    setIsAntiLockFactorDisabled(res.isAntiLockFactorDisabled)
    setIsNeedOdometerCheck(res.isNeedOdometerCheck)
  }, [typeWatch])

  const setStateFromAutocomplete = autoState => {
    let selected = states.filter(i => i.name.toLowerCase() == autoState.toLowerCase())
    if (selected.length > 0) setValue('state', selected[0].code)
  }

  const handleCheckboxChange = event => {
    setChecked(event.target.checked)

    if (event.target.checked) {
      reportGTM('', '', { event: 'add-vin-later-trigger', at: window.location.pathname })
    }
  }

  const validateOwnership = ownershipPeriod => {
    const year = getValues()['year']
    if (year) {
      return validateOwnershipMethod(ownershipPeriod, year)
    }
  }

  const onFormSubmit = async () => {
    let valid = await triggerValidation()
    if (!valid) return

    let values = getValues()
    if (values.new_vehicle_req_details && values.new_vehicle_req_details != 'otherdefault') {
      await awaitWithLoading(vehiclesApi.newVehicleRequest(values))
      reportGTM('', '', { event: 'new-vehicle-req', at: window.location.pathname, vehicle_details: otherWatch })
    }

    if(values.estimated_mileage_value <= 2500){
      reportGTM('', '', {event: 'low_mile_quote'})
    }

    onSave(values)
  }

  React.useEffect(() => {
    if (yearWatch != 'yeardefault' && modelWatch != 'modeldefault') {
      setShowNewVehicleTextField(modelWatch == 'other')
    }
  }, [modelWatch])

  React.useEffect(() => {
    register({ name: 'make' })
    register({ name: 'model' })
    register({ name: 'new_vehicle_req_details' })
    register({ name: 'vin' })
    register({ name: 'engine_cc' })
    register({ name: 'type' })
    register({ name: 'is_motorcycle_include_odometer' })
  }, [register])

  React.useEffect(() => {
    setModels([])
    setMakes([])
    setValue('model', 'modeldefault')
    setValue('make', 'makedefault')

    let inputYear = getValues()['year']
    if (inputYear && inputYear != '') {
      if (inputYear == 'pre 1986') {
        setMakes([])
        setShowNewVehicleTextField(true)
      } else {
        fetchMakes(inputYear)
        setShowNewVehicleTextField(false)
      }
    }

    if (otherWatch && otherWatch != '' && (inputYear == 1900 || inputYear == 'pre 1986')) {
      setShowNewVehicleTextField(true)
    }
  }, [yearWatch])

  React.useEffect(() => {
    setModels([])
    setValue('model', 'modeldefault')

    let inputMake = getValues()['make']
    let inputYear = getValues()['year']
    if (inputYear && inputYear != 'yeardefault' && inputMake && inputMake != 'makedefault') {
      fetchModels(inputMake, inputYear)
    }
  }, [makeWatch])

  React.useEffect(() => {
    let inputModel = getValues()['model']
    let inputYear = getValues()['year']
    if (inputYear && inputYear != 'yeardefault' && inputModel && inputModel != 'modeldefault' && inputModel != 'other') {
      fetchRequestedMotorcycles(inputModel, inputYear)
    }
  }, [modelWatch])

  React.useEffect(() => {
    fetchYears()
    register('id')
    let id = EventEmitterService.subscribe(SAVE_EVENT_KEY, async () => {
      onFormSubmit()
    })
    setIsLojackFactordDisabled(statesWithoutLojackDiscount.includes(state))

    return function cleanup() {
      EventEmitterService.unsubscribe(SAVE_EVENT_KEY, id)
    }
  }, [])

  return (
    <div className="motorcycle-form">
      <FlexView vAlignContent={'center'} className="margin-bottom-basic">
        <MotorcycleSvg className="large-icon margin-right-6" />
        <div className="text-title bold black">Tell us about your motorcycle</div>
      </FlexView>
      <br />
      <Grid container spacing={2} className="margin-bottom-basic">
        <Grid item xs={12} sm={6} md={3}>
          <div className="full-width">
            <Controller
              disabled={years.length == 0}
              rules={{ required: true, validate: x => x && x != 'yeardefault' }}
              name="year"
              as={
                <TextField
                  InputLabelProps={{ className: 'avenir-book' }}
                  inputRef={{ required: true, validate: x => x && x != 'yeardefault' }}
                  error={!!errors.year}
                  fullWidth
                  label="Model Year"
                  select
                  variant="outlined"
                  className=""
                >
                  <MenuItem disabled value={'yeardefault'}>
                    <div className="placeholder-text avenir-book">Model Year</div>
                  </MenuItem>
                  {years.map(item => (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              }
              control={control}
            />
            {!!errors.year && <FormHelperText error={!!errors.year}>Required</FormHelperText>}
          </div>
        </Grid>
        {makes.length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <div className="full-width">
              <Controller
                disabled={makes.length == 0}
                rules={{ required: true, validate: x => x && x != 'makedefault' }}
                name="make"
                as={
                  <TextField
                    InputLabelProps={{ className: 'avenir-book' }}
                    inputRef={{ required: true, validate: x => x && x != 'makedefault' }}
                    error={!!errors.make}
                    fullWidth
                    label="Make"
                    select
                    variant="outlined"
                    className=""
                  >
                    <MenuItem disabled value={'makedefault'}>
                      <div className="placeholder-text avenir-book">Make</div>
                    </MenuItem>
                    <ListSubheader disableSticky>
                      <div className="text-small-title black bolder">Most Common Manufacturers</div>
                    </ListSubheader>
                    {most_common_make_upper
                      .filter(item => makes.includes(item))
                      .map(item => (
                        <MenuItem className="margin-left-basic" key={item} value={item.toUpperCase()}>
                          {item.toUpperCase()}
                        </MenuItem>
                      ))}
                    <ListSubheader disableSticky>
                      <div className="text-small-title black bolder">All Manufacturers</div>
                    </ListSubheader>
                    {makes.map(item => (
                      <MenuItem className="margin-left-basic" key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                }
                control={control}
              />
              {!!errors.make && <FormHelperText error={!!errors.make}>Required</FormHelperText>}
            </div>
          </Grid>
        )}
        {models.length > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <div className="full-width">
              <Controller
                disabled={models.length == 0}
                rules={{ required: true, validate: x => x && x != 'modeldefault' }}
                name="model"
                as={
                  <TextField
                    InputLabelProps={{ className: 'avenir-book' }}
                    inputRef={{ required: true, validate: x => x && x != 'modeldefault' }}
                    error={!!errors.model}
                    fullWidth
                    label="Model"
                    select
                    variant="outlined"
                  >
                    <MenuItem disabled value={'modeldefault'}>
                      <div className="placeholder-text avenir-book">Model</div>
                    </MenuItem>
                    {models.map(item => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </TextField>
                }
                control={control}
              />
              {!!errors.model && <FormHelperText error={!!errors.model}>Required</FormHelperText>}
            </div>
          </Grid>
        )}
      </Grid>
      {showNewVehicleTextField && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <GlobalTextField
              fullWidth
              variant="outlined"
              label="Year, Make, Model"
              name="new_vehicle_req_details"
              inputRef={register({ required: true })}
              errors={errors}
              helperText={errors.new_vehicle_req_details && 'Required'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GlobalTextField
              fullWidth
              type="number"
              variant="outlined"
              label="Motorcycle Actual Cash Value"
              name="value"
              inputRef={register({ required: true })}
              errors={errors}
              helperText={errors.value && 'Required'}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <GlobalTextField
              variant="outlined"
              fullWidth
              inputRef={register({ required: !checked, maxLength: 17, minLength: 17 })}
              onChange={vinValidation}
              name="vin"
              inputProps={{ style: { textTransform: 'uppercase' } }}
              helperText={errors.vin && (errors.vin.type == 'required' ? 'Required' : 'Vin must contain 17 characters')}
              label={'Vehicle identification number (VIN)'}
              errors={errors}
            />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={2} className="margin-top-basic">
        <Grid item xs={12} sm={6} md={3}>
          <FlexView vAlignContent={'center'}>
            <GlobalTextField
              fullWidth
              label="Zip code"
              name="zip"
              inputRef={register({ required: true })}
              register={register}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              errors={errors}
              helperText={errors.other && 'Required'}
            />
            <SkyInformationPopover eventId={'motor-zipcode-popover'}>
              <div className="text-small">Zip code where vehicle is kept</div>
            </SkyInformationPopover>
          </FlexView>
        </Grid>
        {Number.parseInt(yearWatch) > 0 && (
          <Grid item xs={12} sm={6} md={3}>
            <FlexView vAlignContent={'center'}>
              <div className="full-width">
                <Controller
                  control={control}
                  rules={{ validate: validateOwnership, required: true }}
                  name="ownership_period"
                  defaultValue={defaultValues.ownership_period || 'default'}
                  as={
                    <TextField
                      InputLabelProps={{ className: 'avenir-book' }}
                      error={!!errors.ownership_period}
                      variant="outlined"
                      fullWidth
                      label="Ownership period"
                      select
                    >
                      <MenuItem disabled value={'default'}>
                        <div className="placeholder-text avenir-book">Ownership period</div>
                      </MenuItem>
                      <MenuItem value={0}>Less than 1 year ago</MenuItem>
                      {prange(1, 11, 1).map(val => (
                        <MenuItem value={val}>{val}</MenuItem>
                      ))}
                      <MenuItem value={11}>11+</MenuItem>
                    </TextField>
                  }
                />
                {!!errors.ownership_period && (
                  <FormHelperText error={!!errors.ownership_period}>
                    {getValues()['ownership_period'] == 'default' ? 'Required' : 'Ownership period cannot be greater than motorcycle age'}
                  </FormHelperText>
                )}
              </div>
              <SkyInformationPopover eventId={'motor-zipcode-popover'}>
                <div className="text-small">How many years have you owned your bike?</div>
              </SkyInformationPopover>
            </FlexView>
          </Grid>
        )}
        {modelWatch != 'modeldefault' && modelWatch != 'other' && (
          <div hidden>
            <GlobalTextField
              shrink={!!ccWatch}
              disabled={true}
              type="number"
              fullWidth
              label="CC size"
              name="engine_cc"
              inputRef={register({ required: true })}
              register={register}
              inputProps={{ style: { textTransform: 'uppercase' } }}
              errors={errors}
              helperText={errors.engine_cc && errors.engine_cc.type}
            />
          </div>
        )}

        <Grid item xs={12} sm={6} md={3}>
          <FlexView vAlignContent={'center'}>
            <div className="full-width">
              <FlexView column>
                <Controller
                  control={control}
                  name="accessory_value"
                  defaultValue={defaultValues.accessory_value || 0}
                  as={
                    <TextField
                      InputLabelProps={{ className: 'avenir-book' }}
                      // InputProps={{endAdornment: <InputAdornment position="start"> <OptionalTag /> </InputAdornment>}}
                      error={!!errors.accessory_value && 'Required'}
                      variant="outlined"
                      fullWidth
                      label="Accessory Value (optional)"
                      select
                    >
                      <MenuItem disabled value={0}>
                        <div className="placeholder-text">Accessory Value</div>
                      </MenuItem>
                      {prange(0, 11000, 1000).map(val => (
                        <MenuItem value={val}>
                          {FirebaseService.getValue(FirebaseConfigKey.CURRENCY_SYMBOL)}
                          {val.toLocaleString()}
                        </MenuItem>
                      ))}
                    </TextField>
                  }
                />
                {isNewFlow &&
                  (sessionContext.selectedState != 'VA' ? (
                    <div className="text-small eggplantpurple margin-top-small">$3,000 included with comprehensive coverage</div>
                  ) : (
                    <div className="text-small eggplantpurple margin-top-small">$3,000 included with other than collision coverage</div>
                  ))}
              </FlexView>
            </div>
            {isNewFlow && (
              <div className="margin-bottom-large">
                <SkyInformationPopover eventId={'motor-zipcode-popover'}>
                  <div className="text-small">
                    Accessory Coverage provides physical damage protection to riding apparel and equipment, devices, enhancements,
                    accessories, and changes other than those that are original manufacturer installed, which alters the appearance or
                    performance of a vehicle. Comprehensive coverage includes a free accessory limit of up to $3000 for your motorcycle
                  </div>
                </SkyInformationPopover>
              </div>
            )}
          </FlexView>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Controller
            control={control}
            name="side_car_value"
            defaultValue={defaultValues.side_car_value || 0}
            as={
              <TextField
                InputLabelProps={{ className: 'avenir-book' }}
                error={!!errors.side_car_value && 'Required'}
                variant="outlined"
                fullWidth
                // InputProps={{endAdornment: <InputAdornment className="margin-right-basic" position="start"> <OptionalTag /> </InputAdornment>}}
                label="Sidecar value (optional)"
                select
              >
                <MenuItem disabled value={0}>
                  <div className="placeholder-text">Sidecar Value</div>
                </MenuItem>
                {prange(0, 11000, 1000).map(val => (
                  <MenuItem value={val}>
                    {FirebaseService.getValue(FirebaseConfigKey.CURRENCY_SYMBOL)}
                    {val.toLocaleString()}
                  </MenuItem>
                ))}
              </TextField>
            }
          />
        </Grid>
      </Grid>

      {defaultValues.vin && (
        <Grid container spacing={2} className="margin-top-basic">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              InputLabelProps={{ className: 'avenir-book' }}
              variant="outlined"
              fullWidth
              inputRef={register({ maxLength: 17, minLength: yearWatch <= 1980 ? 0 : 17 })}
              onChange={vinValidation}
              name="vin"
              inputProps={{ style: { textTransform: 'uppercase' } }}
              helperText={errors.vin && (errors.vin.type == 'required' ? 'Required' : 'Vin must contain 17 characters')}
              label={'Vehicle identification number (VIN)'}
              error={!!errors.vin}
            />
          </Grid>
        </Grid>
      )}

      <Divider />
      <Grid container spacing={1} alignItems={'center'} className="margin-top-basic">
        <Grid item xs={12} sm={6}>
          <FlexView vAlignContent={'center'}>
            <div className="text-body black">How many miles do you estimate to ride annually on this vehicle?</div>
            <SkyInformationPopover eventId="estimated-mileage-popover">
              <div className="text-small avenir-book">{estimatedToolTip}</div>
            </SkyInformationPopover>
          </FlexView>
          {!!errors.estimated_mileage_value && <div className="text-small red">Required</div>}
        </Grid>
        <Grid item xs={12} sm={3}>
          <FlexView vAlignContent={'center'}>
            <Controller
              control={control}
              name="estimated_mileage_value"
              rules={{ validate: x => x !== 0, required: true }}
              as={
                <TextField
                  className="text-body"
                  InputLabelProps={{ className: 'avenir-book' }}
                  error={!!errors.estimated_mileage_value}
                  variant="outlined"
                  fullWidth
                  select
                >
                  <MenuItem disabled value={0}>
                    <div className="placeholder-text">Annual miles estimation</div>
                  </MenuItem>
                  {getEstimatedMilesOptions(sessionContext.user.affiliateCode,isAdmin).estimatedMileageOption.map(val => (
                    <MenuItem value={val}>{val.toString()}</MenuItem>
                  ))}
                </TextField>
              }
            />
          </FlexView>
        </Grid>
      </Grid>

      {isNeedOdometerCheck && (
        <YesNoRadioButton
          label={'Does your vehicle include an odometer?'}
          name="is_motorcycle_include_odometer"
          defaultValue={defaultValues['is_motorcycle_include_odometer']}
          register={register}
          control={control}
          errors={errors}
          watch={watch}
          tooltip={
            <SkyInformationPopover eventId={'is_motorcycle_include_odometer'}>
              <div className="text-small avenir-book">At the moment we are not able to cover vehicles that don’t include an odometer.</div>
            </SkyInformationPopover>
          }
        />
      )}
      {!isAntiLockFactorDisabled && (
        <YesNoRadioButton
          label={'Does your motorcycle have anti-lock brakes?'}
          name="anti_lock_brakes"
          discount
          defaultValue={defaultValues['anti_lock_brakes']}
          register={register}
          control={control}
          errors={errors}
          watch={watch}
        />
      )}
      {!isLojackFactordDisabled && (
        <YesNoRadioButton
          label={'Does your motorcycle have anti-theft devices?'}
          name="anti_theft_device"
          discount
          defaultValue={defaultValues['anti_theft_device']}
          register={register}
          control={control}
          errors={errors}
          watch={watch}
        />
      )}
      <YesNoRadioButton
        label={'Will you be making payments on this motorcycle? (motorcycle lien)'}
        name="lienholder"
        defaultValue={defaultValues['lienholder']}
        register={register}
        control={control}
        errors={errors}
        watch={watch}
      />
      {lienholderWatch == 'yes' && (
        <form id="lienholder_form">
          <div>
            <div className="text-body blue bold margin-top-basic margin-bottom-basic">Lienholder details</div>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <GlobalTextField
                  fullWidth
                  label="Name"
                  name="lienholder_name"
                  inputRef={register({ required: true })}
                  errors={errors}
                  helperText={!!errors.lienholder_name && 'Required'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GlobalTextField
                  fullWidth
                  label="Address line"
                  name="lienholder_address"
                  inputRef={register({ required: true })}
                  errors={errors}
                  helperText={!!errors.lienholder_address && 'Required'}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <GlobalTextField
                  fullWidth
                  label="City"
                  name="lienholder_city"
                  inputRef={register({ required: true })}
                  errors={errors}
                  helperText={!!errors.lienholder_city && 'Required'}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <div className="full-width margin-right-basic">
                  <Controller
                    rules={{ required: true, validate: x => x && x != 'statedefault' }}
                    name="lienholder_state"
                    as={
                      !isLoadingStates ? (
                        <TextField
                          InputLabelProps={{ className: 'avenir-book' }}
                          error={!!errors.lienholder_state}
                          fullWidth
                          label={LocalizationService.STRINGS.state}
                          select
                          variant="outlined"
                        >
                          <MenuItem disabled value={'statedefault'}>
                            <div className="placeholder-text">{LocalizationService.STRINGS.state}</div>
                          </MenuItem>
                          {[...states]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(item => (
                              <MenuItem key={item.code} value={item.code}>
                                {item.name}
                              </MenuItem>
                            ))}
                        </TextField>
                      ) : (
                        <CircularProgress />
                      )
                    }
                    control={control}
                  />
                  {!!errors.lienholder_state && (
                    <FormHelperText error={!!errors.lienholder_state}>
                      {errors.lienholder_state.type == 'validate' ? 'Not available' : 'Required'}
                    </FormHelperText>
                  )}
                </div>
                <TextField
                  InputLabelProps={{ className: 'avenir-book' }}
                  name="lienholder_state"
                  onChange={e => {
                    setStateFromAutocomplete(e.target.value)
                  }}
                  style={{ height: 0, width: 0 }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <GlobalTextField
                  fullWidth
                  label="Zip code"
                  name="lienholder_zipcode"
                  inputRef={register({ required: true })}
                  errors={errors}
                  error={!!errors.lienholder_city}
                  helperText={!!errors.lienholder_zipcode}
                />
              </Grid>
            </Grid>
          </div>
        </form>
      )}

      <FlexView hAlignContent={'center'} className="margin-top-basic">
        {/* <Button onClick={onFormSubmit} className='text-body glowgreen bold' style={{textTransform: 'none'}} type='submit'>Save</Button> */}
        <NewFillButton onClick={onFormSubmit} flat>
          Save
        </NewFillButton>
      </FlexView>
    </div>
  )
}
